import { type AskPunttProjectData } from "@mg/schemas/src/christo/askPuntt";
import {
  ArrowSquareOut,
  BellRinging,
  CalendarBlank,
  Check,
  CheckCircle,
  ClockCountdown,
  Lightning,
  MagnifyingGlass,
  PencilSimple,
  Play,
  Rocket,
  Seal,
  ShieldCheck,
  Timer,
  Users,
  Warning,
  X,
} from "@phosphor-icons/react";
import {
  Badge,
  Button,
  Flex,
  IconButton,
  Text,
  Tooltip,
} from "@radix-ui/themes";
import { useCallback, useEffect, useMemo, useState } from "react";

import { updateProjectAction } from "../../../services/askPuntt";
import { useTicketViewData } from "../hooks/useTicketView";

const TICKET_STATUS_COLORS: Record<
  string,
  "green" | "orange" | "blue" | "gray" | "red"
> = {
  approved: "green",
  complete: "green",
  needs_review: "orange",
  wip: "blue",
  incomplete: "red",
};

const TICKET_STATUS_LABELS: Record<string, string> = {
  approved: "Approved",
  complete: "Complete",
  needs_review: "Needs Review",
  wip: "In Progress",
  incomplete: "Incomplete",
};

interface ProjectStatusPaneProps {
  projectData: AskPunttProjectData;
  activeTicketId?: string | null;
  ticketIds?: string[];
  onTicketSelect?: (ticketId: string) => void;
  chatId: string;
  chatCreatedAt?: string;
  onDismiss?: () => void;
  onSendMessage?: (message: string) => void;
  onProjectDataUpdate?: (data: AskPunttProjectData) => void;
  actionsDisabled?: boolean;
}

function stageBarClasses(stage: { status: string; color: string }): string {
  const base = "h-2.5 flex-1 rounded-full transition-all duration-300";
  switch (stage.status) {
    case "done":
      return `${base} bg-malachite-500`;
    case "active":
      return `${base} bg-puntt-gold-9 animate-pulse`;
    case "needs_review":
      return `${base} bg-puntt-red-9 ring-2 ring-puntt-red-5`;
    case "blocked":
      return `${base} bg-puntt-red-9`;
    default:
      return `${base} bg-puntt-neutral-gray-5`;
  }
}

function stageLabelWeight(status: string): "bold" | "medium" | "regular" {
  if (status === "needs_review" || status === "active") return "bold";
  if (status === "done") return "medium";
  return "regular";
}

function stageLabelColor(status: string): string {
  if (status === "needs_review") return "text-puntt-red-11";
  if (status === "done") return "text-malachite-700";
  if (status === "active") return "text-puntt-gold-11";
  return "text-puntt-neutral-gray-9";
}

export function formatDuration(ms: number): string {
  const absMs = Math.abs(ms);
  const hours = Math.floor(absMs / (1000 * 60 * 60));
  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;

  if (days === 0 && hours === 0) return "< 1h";
  if (days === 0) return `${hours}h`;
  if (remainingHours === 0) return `${days}d`;
  return `${days}d ${remainingHours}h`;
}

export function deadlineColor(msRemaining: number): string {
  if (msRemaining < 0) return "text-puntt-red-9";
  const daysRemaining = msRemaining / (1000 * 60 * 60 * 24);
  if (daysRemaining <= 2) return "text-puntt-gold-11";
  return "text-puntt-neutral-gray-12";
}

export function deadlineBg(msRemaining: number): string {
  if (msRemaining < 0) return "bg-puntt-red-2 border-puntt-red-5";
  const daysRemaining = msRemaining / (1000 * 60 * 60 * 24);
  if (daysRemaining <= 2) return "bg-puntt-gold-2 border-puntt-gold-5";
  return "bg-puntt-neutral-gray-2 border-puntt-neutral-gray-4";
}

function ReviewerRow({
  reviewer,
  onOverride,
  actionsDisabled,
}: {
  reviewer: AskPunttProjectData["routing"][0];
  onOverride?: (role: string) => void;
  actionsDisabled?: boolean;
}) {
  const isDone = reviewer.status === "done";
  const needsReview = reviewer.status === "needs_review";
  const isActive = reviewer.status === "active";
  const isPending = reviewer.status === "pending";
  const [notified, setNotified] = useState(false);

  return (
    <Flex
      align="center"
      gap="3"
      className={`py-2.5 ${needsReview ? "rounded-lg border-l-2 border-l-puntt-red-9 pl-2.5" : ""}`}
      data-testid="project-status-reviewer-row"
    >
      <div
        className={`flex size-8 shrink-0 items-center justify-center rounded-full ${
          isDone
            ? "bg-malachite-100"
            : needsReview
              ? "bg-puntt-red-3"
              : "bg-puntt-neutral-gray-4"
        }`}
      >
        <Text
          size="1"
          weight="bold"
          className={
            isDone
              ? "text-malachite-700"
              : needsReview
                ? "text-puntt-red-11"
                : "text-puntt-neutral-gray-11"
          }
        >
          {reviewer.initials}
        </Text>
      </div>
      <div className="min-w-0 flex-1">
        <Text
          size="2"
          weight={needsReview || isActive ? "bold" : "medium"}
          className={
            isPending
              ? "text-puntt-neutral-gray-9"
              : "text-puntt-neutral-gray-12"
          }
        >
          {reviewer.name}
        </Text>
        <Text as="span" size="1" className="ml-1 text-puntt-neutral-gray-9">
          · {reviewer.role}
        </Text>
      </div>
      {isDone ? (
        <>
          <Badge size="1" variant="soft" color="green">
            Human review not needed
          </Badge>
          <Check
            size={14}
            weight="bold"
            className="shrink-0 text-malachite-600"
          />
        </>
      ) : needsReview ? (
        <Flex align="center" gap="2">
          {notified ? (
            <Badge size="1" variant="soft" color="amber">
              Notified
            </Badge>
          ) : (
            <Badge size="1" variant="soft" color="red">
              Needs Review
            </Badge>
          )}
          {!notified && (
            <Tooltip content={`Notify ${reviewer.name}`}>
              <IconButton
                size="1"
                variant="ghost"
                color="amber"
                className="cursor-pointer"
                disabled={actionsDisabled}
                onClick={() => setNotified(true)}
              >
                <BellRinging size={14} weight="fill" />
              </IconButton>
            </Tooltip>
          )}
          {onOverride && (
            <Tooltip content={`Override ${reviewer.role}`}>
              <IconButton
                size="1"
                variant="ghost"
                color="gray"
                className="cursor-pointer"
                disabled={actionsDisabled}
                onClick={() => onOverride(reviewer.role)}
              >
                <ShieldCheck size={14} weight="fill" />
              </IconButton>
            </Tooltip>
          )}
        </Flex>
      ) : isActive ? (
        <>
          <Badge size="1" variant="soft" color="yellow">
            In Review
          </Badge>
          <span className="size-2.5 shrink-0 animate-pulse rounded-full bg-puntt-gold-9" />
        </>
      ) : (
        <>
          <Badge size="1" variant="soft" color="gray">
            Pending
          </Badge>
          <span className="size-2.5 shrink-0 rounded-full bg-puntt-neutral-gray-5" />
        </>
      )}
    </Flex>
  );
}

export function deriveTicketDisplayStatus(
  ticketStatus: string,
  stages: AskPunttProjectData["stages"],
): { label: string; color: "green" | "orange" | "blue" | "gray" | "red" } {
  const allDone = stages.length > 0 && stages.every((s) => s.status === "done");
  const anyNeedsReview = stages.some((s) => s.status === "needs_review");
  const anyActive = stages.some((s) => s.status === "active");
  const nonFinal = stages.filter((s) => s.name.toLowerCase() !== "final");
  const readyForCompletion =
    !allDone &&
    nonFinal.length > 0 &&
    nonFinal.every((s) => s.status === "done");

  if (ticketStatus === "approved" || ticketStatus === "complete") {
    return { label: "Approved", color: "green" };
  }
  if (allDone) {
    return { label: "Human review not needed", color: "green" };
  }
  if (readyForCompletion) {
    return { label: "Ready for approval", color: "green" };
  }
  if (anyNeedsReview) {
    return { label: "Needs Attention", color: "orange" };
  }
  if (anyActive) {
    return { label: "Review In Progress", color: "blue" };
  }
  return {
    label: TICKET_STATUS_LABELS[ticketStatus] ?? ticketStatus,
    color: TICKET_STATUS_COLORS[ticketStatus] ?? "gray",
  };
}

function TicketCardSkeleton() {
  return (
    <div
      className="mb-3 flex animate-pulse items-center gap-3 rounded-lg border border-puntt-neutral-gray-4 p-3"
      data-testid="project-status-ticket-card-skeleton"
    >
      <div className="size-10 shrink-0 rounded-md bg-puntt-neutral-gray-4" />
      <div className="min-w-0 flex-1">
        <div className="mb-1.5 h-4 w-3/4 rounded bg-puntt-neutral-gray-4" />
        <div className="h-3 w-1/3 rounded bg-puntt-neutral-gray-4" />
      </div>
    </div>
  );
}

function TicketCard({
  ticketId,
  stages,
  onSelect,
}: {
  ticketId: string;
  stages: AskPunttProjectData["stages"];
  onSelect?: (ticketId: string) => void;
}) {
  const { ticket, isLoading } = useTicketViewData(ticketId);

  if (isLoading) return <TicketCardSkeleton />;
  if (!ticket) return null;

  const rawStatus = ticket.status ?? "wip";
  const { label, color } = deriveTicketDisplayStatus(rawStatus, stages);
  const firstReviewFile = ticket.revisionBoards
    ?.filter((b: { deletedAt?: unknown }) => !b.deletedAt)
    ?.at(-1)?.reviewFiles?.[0] as { thumbnailUrl?: string } | undefined;
  const thumbUrl = firstReviewFile?.thumbnailUrl;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onSelect?.(ticketId)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect?.(ticketId);
        }
      }}
      className="mb-3 flex cursor-pointer items-center gap-3 rounded-lg border border-puntt-neutral-gray-4 p-3 transition-colors hover:bg-puntt-neutral-gray-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-puntt-accent-9"
      data-testid="project-status-ticket-card"
      aria-label={`View ticket: ${ticket.title}`}
    >
      {thumbUrl && (
        <img
          src={thumbUrl}
          alt=""
          className="size-10 shrink-0 rounded-md object-cover"
        />
      )}
      <div className="min-w-0 flex-1 overflow-hidden">
        <Text
          as="div"
          size="2"
          weight="medium"
          className="w-full min-w-0 truncate text-puntt-neutral-gray-12"
        >
          {ticket.title}
        </Text>
        <Flex align="center" gap="2" className="mt-0.5">
          <Badge size="1" variant="soft" color={color}>
            {label}
          </Badge>
        </Flex>
      </div>
      <a
        href={`/tickets/${ticketId}/view?version=0`}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        className="shrink-0 rounded p-1 text-puntt-neutral-gray-9 transition-colors hover:bg-puntt-neutral-gray-3 hover:text-puntt-neutral-gray-12"
        aria-label="Open ticket in new tab"
      >
        <ArrowSquareOut size={14} />
      </a>
    </div>
  );
}

function TimelineBlock({
  chatCreatedAt,
  targetDeadline,
  completedAt,
}: {
  chatCreatedAt?: string;
  targetDeadline?: string;
  completedAt?: string;
}) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (completedAt) return;
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, [completedAt]);

  const timeline = useMemo(() => {
    const created = chatCreatedAt ? new Date(chatCreatedAt).getTime() : null;
    const deadline = targetDeadline ? new Date(targetDeadline).getTime() : null;
    const completed = completedAt ? new Date(completedAt).getTime() : null;

    return { now, created, deadline, completed };
  }, [now, chatCreatedAt, targetDeadline, completedAt]);

  const { created, deadline, completed } = timeline;

  if (completed && created) {
    const actualDuration = completed - created;
    const expectedDuration = deadline ? deadline - created : null;
    const delta = expectedDuration ? expectedDuration - actualDuration : null;

    return (
      <div className="mb-5 space-y-2" data-testid="project-status-timeline">
        <Flex
          align="center"
          gap="2"
          className="rounded-lg border border-malachite-200 bg-malachite-50 px-3 py-2.5"
        >
          <CheckCircle
            size={16}
            weight="fill"
            className="shrink-0 text-malachite-600"
          />
          <div className="min-w-0 flex-1">
            <Text size="2" weight="medium" className="text-malachite-800">
              Completed in {formatDuration(actualDuration)}
            </Text>
          </div>
        </Flex>
        {delta !== null && (
          <Flex justify="between" align="center" className="px-1">
            <Text size="1" className="text-puntt-neutral-gray-9">
              vs deadline
            </Text>
            <Text
              size="2"
              weight="bold"
              className={delta >= 0 ? "text-malachite-700" : "text-puntt-red-9"}
            >
              {delta >= 0
                ? `${formatDuration(delta)} ahead`
                : `${formatDuration(Math.abs(delta))} behind`}
            </Text>
          </Flex>
        )}
      </div>
    );
  }

  return (
    <div className="mb-5 space-y-2" data-testid="project-status-timeline">
      {created && (
        <Flex justify="between" align="center" className="px-1">
          <Flex align="center" gap="1.5">
            <Timer size={14} className="text-puntt-neutral-gray-9" />
            <Text size="2" className="text-puntt-neutral-gray-11">
              Elapsed
            </Text>
          </Flex>
          <Text size="2" weight="medium" className="text-puntt-neutral-gray-12">
            {formatDuration(now - created)}
          </Text>
        </Flex>
      )}
      {deadline && (
        <Flex
          align="center"
          gap="2"
          className={`rounded-lg border px-3 py-2.5 ${deadlineBg(deadline - now)}`}
        >
          <CalendarBlank
            size={14}
            className={`shrink-0 ${deadlineColor(deadline - now)}`}
          />
          <div className="min-w-0 flex-1">
            <Text
              size="2"
              weight="medium"
              className={deadlineColor(deadline - now)}
            >
              {deadline - now > 0
                ? `Deadline in ${formatDuration(deadline - now)}`
                : `Deadline passed ${formatDuration(now - deadline)} ago`}
            </Text>
          </div>
          {deadline - now > 0 && (
            <ClockCountdown
              size={14}
              className={`shrink-0 ${deadlineColor(deadline - now)}`}
            />
          )}
        </Flex>
      )}
    </div>
  );
}

function ActionStatus({
  stages,
  routing,
  hasTicket,
  onSendMessage,
  onCompleteProject,
  onOverrideAll,
  actionsDisabled,
}: {
  stages: AskPunttProjectData["stages"];
  routing: AskPunttProjectData["routing"];
  hasTicket: boolean;
  onSendMessage?: (message: string) => void;
  onCompleteProject?: () => void;
  onOverrideAll?: () => void;
  actionsDisabled?: boolean;
}) {
  const [reviewersNotified, setReviewersNotified] = useState(false);
  const needsReview = stages.filter((s) => s.status === "needs_review");
  const active = stages.filter((s) => s.status === "active");
  const blocked = stages.filter((s) => s.status === "blocked");
  const allDone = stages.length > 0 && stages.every((s) => s.status === "done");
  const allPending =
    stages.length > 0 && stages.every((s) => s.status === "pending");
  const nonFinal = stages.filter((s) => s.name.toLowerCase() !== "final");
  const readyForCompletion =
    !allDone &&
    nonFinal.length > 0 &&
    nonFinal.every((s) => s.status === "done");

  if (allDone) {
    return (
      <div
        className="mb-5 rounded-lg border border-malachite-200 bg-malachite-50 px-3 py-2.5"
        data-testid="project-status-action-banner"
      >
        <Flex align="center" gap="2">
          <CheckCircle
            size={14}
            weight="fill"
            className="shrink-0 text-malachite-600"
          />
          <Text size="2" weight="medium" className="text-malachite-800">
            Project completed
          </Text>
        </Flex>
      </div>
    );
  }

  if (readyForCompletion) {
    return (
      <div className="mb-5 space-y-3">
        <div className="rounded-lg border border-malachite-200 bg-malachite-50 px-3 py-2.5">
          <Flex align="center" gap="2">
            <Check
              size={14}
              weight="bold"
              className="shrink-0 text-malachite-600"
            />
            <Text size="2" weight="medium" className="text-malachite-800">
              All stages clear — ready for final approval
            </Text>
          </Flex>
        </div>
        {onCompleteProject && (
          <Button
            size="2"
            color="green"
            className="w-full cursor-pointer"
            disabled={actionsDisabled}
            onClick={onCompleteProject}
          >
            <CheckCircle size={14} weight="fill" />
            Complete Project
          </Button>
        )}
      </div>
    );
  }

  if (blocked.length > 0) {
    return (
      <div
        className="mb-5 rounded-lg border border-puntt-red-5 bg-puntt-red-2 px-3 py-2.5"
        data-testid="project-status-action-banner"
      >
        <Flex align="center" gap="2">
          <Warning
            size={14}
            weight="fill"
            className="shrink-0 text-puntt-red-9"
          />
          <Text size="2" weight="medium" className="text-puntt-red-11">
            Blocked — {blocked.map((s) => s.name).join(" & ")}
          </Text>
        </Flex>
      </div>
    );
  }

  if (needsReview.length > 0) {
    const reviewerNames = routing
      .filter((r) => {
        const role = r.role.toLowerCase();
        return needsReview.some((s) => {
          const name = s.name.toLowerCase();
          return (
            role === name || role.startsWith(name) || name.startsWith(role)
          );
        });
      })
      .map((r) => r.name);

    return (
      <div
        className="mb-5 space-y-3"
        data-testid="project-status-action-banner"
      >
        <div className="rounded-lg border border-puntt-gold-5 bg-puntt-gold-2 px-3 py-2.5">
          <Flex align="start" gap="2">
            <Warning
              size={14}
              weight="fill"
              className="mt-0.5 shrink-0 text-puntt-gold-9"
            />
            <div>
              <Text size="2" weight="medium" className="text-puntt-gold-11">
                {needsReview.map((s) => s.name).join(" & ")}{" "}
                {needsReview.length === 1 ? "needs" : "need"} human review
              </Text>
              {reviewerNames.length > 0 && (
                <Text as="p" size="1" className="mt-0.5 text-puntt-gold-9">
                  Assigned to {reviewerNames.join(", ")}
                </Text>
              )}
            </div>
          </Flex>
        </div>
        <Flex gap="2">
          <Button
            size="2"
            variant="outline"
            color={reviewersNotified ? "green" : "amber"}
            className="flex-1 cursor-pointer"
            disabled={actionsDisabled || reviewersNotified}
            onClick={() => setReviewersNotified(true)}
          >
            {reviewersNotified ? (
              <>
                <Check size={14} weight="bold" />
                Reviewers Notified
              </>
            ) : (
              <>
                <BellRinging size={14} weight="fill" />
                Notify Reviewers
              </>
            )}
          </Button>
          {onOverrideAll && (
            <Button
              size="2"
              variant="outline"
              className="flex-1 cursor-pointer"
              disabled={actionsDisabled}
              onClick={onOverrideAll}
            >
              <ShieldCheck size={14} weight="fill" />
              Override & Approve All
            </Button>
          )}
        </Flex>
      </div>
    );
  }

  if (active.length > 0) {
    return (
      <div
        className="mb-5 rounded-lg border border-puntt-gold-5 bg-puntt-gold-2 px-3 py-2.5"
        data-testid="project-status-action-banner"
      >
        <Flex align="center" gap="2">
          <span className="size-2.5 shrink-0 animate-pulse rounded-full bg-puntt-gold-9" />
          <Text size="2" weight="medium" className="text-puntt-gold-11">
            AI review in progress across {active.length} stage
            {active.length !== 1 ? "s" : ""}
          </Text>
        </Flex>
      </div>
    );
  }

  if (allPending && !hasTicket) {
    return (
      <div
        className="mb-5 space-y-3"
        data-testid="project-status-action-banner"
      >
        <div className="rounded-lg border border-puntt-accent-5 bg-puntt-accent-2 px-3 py-2.5">
          <Flex align="center" gap="2">
            <Lightning
              size={14}
              weight="fill"
              className="shrink-0 text-puntt-accent-9"
            />
            <Text size="2" weight="medium" className="text-puntt-accent-11">
              AI-generated plan — review and approve to start
            </Text>
          </Flex>
        </div>
        {onSendMessage && (
          <Flex gap="2">
            <Button
              size="2"
              className="flex-1 cursor-pointer"
              disabled={actionsDisabled}
              onClick={() => onSendMessage("Start project")}
            >
              <Play size={14} weight="fill" />
              Approve & Start
            </Button>
            <Button
              size="2"
              variant="outline"
              className="cursor-pointer"
              disabled={actionsDisabled}
              onClick={() =>
                onSendMessage("I want to change something about this plan")
              }
            >
              <PencilSimple size={14} weight="bold" />
              Edit
            </Button>
          </Flex>
        )}
      </div>
    );
  }

  if (allPending) {
    return (
      <div
        className="mb-5 rounded-lg border border-puntt-neutral-gray-4 bg-puntt-neutral-gray-2 px-3 py-2.5"
        data-testid="project-status-action-banner"
      >
        <Flex align="center" gap="2">
          <ClockCountdown
            size={14}
            className="shrink-0 text-puntt-neutral-gray-9"
          />
          <Text size="2" weight="medium" className="text-puntt-neutral-gray-11">
            Waiting to begin review
          </Text>
        </Flex>
      </div>
    );
  }

  return null;
}

function ConfidenceBadge({ confidence }: { confidence: number }) {
  let color: "green" | "orange" | "red" | "gray";
  let label: string;
  if (confidence >= 85) {
    color = "green";
    label = "High confidence";
  } else if (confidence >= 60) {
    color = "orange";
    label = "Medium confidence";
  } else if (confidence >= 30) {
    color = "red";
    label = "Low confidence";
  } else {
    color = "gray";
    label = "Very low confidence";
  }

  return (
    <Badge size="1" variant="soft" color={color}>
      {label} · {confidence}%
    </Badge>
  );
}

const PLAN_STEP_ICONS: Record<string, React.ReactNode> = {
  "AI Review": (
    <MagnifyingGlass size={16} weight="bold" className="text-puntt-accent-9" />
  ),
  "Next Steps": (
    <Users size={16} weight="bold" className="text-puntt-accent-9" />
  ),
  "Final Approval": (
    <Seal size={16} weight="bold" className="text-puntt-accent-9" />
  ),
};

function DraftPlanView({
  projectData,
  onSendMessage,
  actionsDisabled,
}: {
  projectData: AskPunttProjectData;
  onSendMessage?: (message: string) => void;
  actionsDisabled?: boolean;
}) {
  const planSteps = projectData.planSteps ?? [];
  const stages = projectData.stages.filter((s) => s.name !== "Final");

  const [reviewMode, setReviewMode] = useState<"full" | "quick" | null>(null);
  const [selectedAreas, setSelectedAreas] = useState<Set<string>>(
    () => new Set(stages.map((s) => s.name)),
  );

  const toggleArea = (name: string) => {
    setSelectedAreas((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const handleApprove = () => {
    if (!onSendMessage) return;
    if (reviewMode === "quick" && selectedAreas.size > 0) {
      const areas = Array.from(selectedAreas).join(", ");
      onSendMessage(`Start project with a quick review focusing on: ${areas}`);
    } else {
      onSendMessage("Start project");
    }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex-1 space-y-3">
        {planSteps.map((step, idx) => {
          const isReviewStep = idx === 0;
          return (
            <div
              key={step.title}
              className="rounded-xl border border-puntt-neutral-gray-4 bg-puntt-neutral-gray-1 p-4"
            >
              <Flex align="center" gap="2.5" className="mb-2">
                <div className="flex size-7 shrink-0 items-center justify-center rounded-lg bg-puntt-accent-2 ring-1 ring-puntt-accent-5/50">
                  {PLAN_STEP_ICONS[step.title] ?? (
                    <Rocket
                      size={14}
                      weight="bold"
                      className="text-puntt-accent-9"
                    />
                  )}
                </div>
                <Text
                  size="2"
                  weight="bold"
                  className="text-puntt-neutral-gray-12"
                >
                  {idx + 1}. {step.title}
                </Text>
              </Flex>
              <Text
                as="p"
                size="2"
                className="leading-relaxed text-puntt-neutral-gray-11"
              >
                {step.description}
              </Text>

              {isReviewStep && stages.length > 0 && (
                <div className="mt-3">
                  {onSendMessage && (
                    <Flex gap="2" className="mb-3">
                      <button
                        disabled={actionsDisabled}
                        onClick={() => {
                          setReviewMode("full");
                          setSelectedAreas(new Set(stages.map((s) => s.name)));
                        }}
                        className={`flex flex-1 flex-col items-start rounded-lg border px-3 py-2.5 text-left transition-all disabled:opacity-50 ${
                          reviewMode !== "quick"
                            ? "border-puntt-accent-7 bg-puntt-accent-2"
                            : "border-puntt-neutral-gray-5 bg-base-white hover:border-puntt-neutral-gray-7 hover:bg-puntt-neutral-gray-2"
                        }`}
                      >
                        <Text
                          size="2"
                          weight="bold"
                          className={
                            reviewMode !== "quick"
                              ? "text-puntt-accent-11"
                              : "text-puntt-neutral-gray-12"
                          }
                        >
                          Full review
                        </Text>
                        <Text size="1" className="text-puntt-neutral-gray-9">
                          {stages.length} areas · ~10-15 min
                        </Text>
                      </button>
                      <button
                        disabled={actionsDisabled}
                        onClick={() => setReviewMode("quick")}
                        className={`flex flex-1 flex-col items-start rounded-lg border px-3 py-2.5 text-left transition-all disabled:opacity-50 ${
                          reviewMode === "quick"
                            ? "border-puntt-accent-7 bg-puntt-accent-2"
                            : "border-puntt-neutral-gray-5 bg-base-white hover:border-puntt-neutral-gray-7 hover:bg-puntt-neutral-gray-2"
                        }`}
                      >
                        <Text
                          size="2"
                          weight="bold"
                          className={
                            reviewMode === "quick"
                              ? "text-puntt-accent-11"
                              : "text-puntt-neutral-gray-12"
                          }
                        >
                          Quick review
                        </Text>
                        <Text size="1" className="text-puntt-neutral-gray-9">
                          ~2-3 min per area
                        </Text>
                      </button>
                    </Flex>
                  )}

                  <Flex wrap="wrap" gap="1.5">
                    {stages.map((s) => {
                      const isSelected = selectedAreas.has(s.name);
                      const isQuickMode = reviewMode === "quick";
                      return (
                        <button
                          key={s.name}
                          type="button"
                          disabled={!isQuickMode || actionsDisabled}
                          onClick={() => toggleArea(s.name)}
                          className={`rounded-full border px-2.5 py-1 text-xs font-medium transition-all ${
                            isQuickMode
                              ? isSelected
                                ? "border-puntt-accent-7 bg-puntt-accent-3 text-puntt-accent-11"
                                : "border-puntt-neutral-gray-5 bg-base-white text-puntt-neutral-gray-9 hover:border-puntt-neutral-gray-7"
                              : "border-puntt-neutral-gray-4 bg-puntt-neutral-gray-2 text-puntt-neutral-gray-11"
                          }`}
                        >
                          {isQuickMode && isSelected && (
                            <Check
                              size={10}
                              weight="bold"
                              className="mr-1 inline"
                            />
                          )}
                          {s.name}
                        </button>
                      );
                    })}
                  </Flex>
                  {reviewMode === "quick" && (
                    <Text
                      as="p"
                      size="1"
                      className="mt-2 text-puntt-neutral-gray-9"
                    >
                      {selectedAreas.size === 0
                        ? "Select at least one area"
                        : `${selectedAreas.size} of ${stages.length} areas · ~${selectedAreas.size * 2}-${selectedAreas.size * 3} min`}
                    </Text>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {projectData.aiRecommendation && (
          <div className="rounded-xl border border-puntt-accent-5/50 bg-puntt-accent-2/30 p-4">
            <Flex align="center" gap="2" className="mb-1.5">
              <Lightning
                size={14}
                weight="fill"
                className="text-puntt-accent-9"
              />
              <Text size="1" weight="bold" className="text-puntt-accent-11">
                AI recommendation
              </Text>
            </Flex>
            <Text
              size="2"
              className="leading-relaxed text-puntt-neutral-gray-12"
            >
              {projectData.aiRecommendation}
            </Text>
          </div>
        )}
      </div>

      {onSendMessage && (
        <div className="mt-5 border-t border-puntt-neutral-gray-4 pt-4">
          <Flex gap="2">
            <Button
              size="2"
              className="flex-1 cursor-pointer"
              disabled={
                actionsDisabled ||
                (reviewMode === "quick" && selectedAreas.size === 0)
              }
              onClick={handleApprove}
            >
              <Play size={14} weight="fill" />
              Approve & Start
            </Button>
            <Button
              size="2"
              variant="outline"
              className="cursor-pointer"
              disabled={actionsDisabled}
              onClick={() =>
                onSendMessage("I want to change something about this plan")
              }
            >
              <PencilSimple size={14} weight="bold" />
              Edit
            </Button>
          </Flex>
        </div>
      )}
    </div>
  );
}

function TicketStatusDot({ ticketId }: { ticketId: string }) {
  const { ticket } = useTicketViewData(ticketId);
  if (!ticket) return null;
  const status = ticket.status ?? "wip";
  const color = TICKET_STATUS_COLORS[status] ?? "gray";
  const label = TICKET_STATUS_LABELS[status] ?? status;
  return (
    <Tooltip content={label}>
      <div
        className="size-2.5 rounded-full"
        style={{
          backgroundColor: `var(--${color}-9, var(--gray-9))`,
        }}
      />
    </Tooltip>
  );
}

export function ProjectStatusPane({
  projectData,
  activeTicketId,
  ticketIds,
  onTicketSelect,
  chatId,
  chatCreatedAt,
  onDismiss,
  onSendMessage,
  onProjectDataUpdate,
  actionsDisabled,
}: ProjectStatusPaneProps) {
  const { stages, routing } = projectData;
  const isPlanPending =
    stages.every((s) => s.status === "pending") && !activeTicketId;
  const [actionLoading, setActionLoading] = useState(false);

  const handleOverrideStage = useCallback(
    async (stageName: string) => {
      setActionLoading(true);
      try {
        const result = await updateProjectAction(
          chatId,
          "override_stage",
          stageName,
        );
        onProjectDataUpdate?.(result.projectData);
      } catch (err) {
        console.error("Failed to override stage:", err);
      } finally {
        setActionLoading(false);
      }
    },
    [chatId, onProjectDataUpdate],
  );

  const handleCompleteProject = useCallback(async () => {
    setActionLoading(true);
    try {
      const result = await updateProjectAction(chatId, "complete_project");
      onProjectDataUpdate?.(result.projectData);
    } catch (err) {
      console.error("Failed to complete project:", err);
    } finally {
      setActionLoading(false);
    }
  }, [chatId, onProjectDataUpdate]);

  return (
    <div
      className="flex h-full flex-col overflow-hidden bg-base-white"
      data-testid="project-status-pane"
    >
      {/* Header */}
      <Flex
        align="center"
        justify="between"
        className="shrink-0 border-b border-puntt-neutral-gray-4 px-5 py-4"
      >
        <div>
          <Flex align="center" gap="2">
            <Text size="4" weight="bold" className="text-puntt-neutral-gray-12">
              {projectData.name}
            </Text>
            {isPlanPending && (
              <Badge size="1" variant="outline" color="blue">
                Draft
              </Badge>
            )}
          </Flex>
          <Flex align="center" gap="2" className="mt-0.5">
            <Text size="2" className="text-puntt-neutral-gray-9">
              {(projectData.markets ?? []).length > 0
                ? (projectData.markets ?? []).join(", ") + " · "
                : ""}
              {projectData.projectType}
            </Text>
            {projectData.confidence != null && (
              <ConfidenceBadge confidence={projectData.confidence} />
            )}
          </Flex>
        </div>
        {onDismiss && (
          <button
            onClick={onDismiss}
            className="rounded-lg p-1.5 text-puntt-neutral-gray-9 transition-colors hover:bg-puntt-neutral-gray-3 hover:text-puntt-neutral-gray-12"
            data-testid="project-status-dismiss-btn"
          >
            <X size={16} />
          </button>
        )}
      </Flex>

      <div className="flex-1 overflow-y-auto p-5">
        {isPlanPending && (projectData.planSteps?.length ?? 0) > 0 ? (
          <DraftPlanView
            projectData={projectData}
            onSendMessage={onSendMessage}
            actionsDisabled={actionsDisabled}
          />
        ) : (
          <>
            {(ticketIds?.length ?? 0) > 0 ? (
              <div className="mb-5" data-testid="project-tickets-section">
                <Flex align="center" justify="between" className="mb-2">
                  <Text
                    size="1"
                    weight="bold"
                    className="uppercase tracking-wider text-puntt-neutral-gray-9"
                  >
                    Tickets ({ticketIds!.length})
                  </Text>
                  <Flex align="center" gap="1">
                    {ticketIds!.map((id) => (
                      <TicketStatusDot key={id} ticketId={id} />
                    ))}
                  </Flex>
                </Flex>
                {ticketIds!.map((id) => (
                  <TicketCard
                    key={id}
                    ticketId={id}
                    stages={stages}
                    onSelect={onTicketSelect}
                  />
                ))}
              </div>
            ) : activeTicketId ? (
              <TicketCard
                ticketId={activeTicketId}
                stages={stages}
                onSelect={onTicketSelect}
              />
            ) : null}

            {/* Timeline */}
            <Text
              size="1"
              weight="bold"
              className="mb-3 uppercase tracking-wider text-puntt-neutral-gray-9"
            >
              Timeline
            </Text>
            <TimelineBlock
              chatCreatedAt={chatCreatedAt}
              targetDeadline={projectData.targetDeadline}
              completedAt={projectData.completedAt}
            />

            {/* Action Status */}
            <ActionStatus
              stages={stages}
              routing={routing}
              hasTicket={!!activeTicketId || (ticketIds?.length ?? 0) > 0}
              onSendMessage={onSendMessage}
              onCompleteProject={handleCompleteProject}
              onOverrideAll={handleCompleteProject}
              actionsDisabled={actionsDisabled || actionLoading}
            />

            {/* Stage Progress Bar */}
            <div className="mb-5" data-testid="project-status-stage-bar">
              <Flex gap="1">
                {stages.map((stage) => (
                  <div
                    key={stage.name}
                    className={stageBarClasses(stage)}
                    title={`${stage.name}: ${stage.status}`}
                  />
                ))}
              </Flex>
              <Flex gap="1" className="mt-1">
                {stages.map((stage) => (
                  <Text
                    key={stage.name}
                    size="1"
                    weight={stageLabelWeight(stage.status)}
                    className={`flex-1 text-center ${stageLabelColor(stage.status)}`}
                  >
                    {stage.name}
                  </Text>
                ))}
              </Flex>
            </div>

            {/* Routing Section */}
            <Flex
              align="center"
              justify="between"
              className="mb-2 border-t border-puntt-neutral-gray-4 pt-4"
            >
              <Text
                size="1"
                weight="bold"
                className="uppercase tracking-wider text-puntt-neutral-gray-9"
              >
                Routing · {routing.length} Reviewers
              </Text>
            </Flex>
            <div className="divide-y divide-puntt-neutral-gray-3">
              {routing.map((reviewer) => (
                <ReviewerRow
                  key={reviewer.name}
                  reviewer={reviewer}
                  onOverride={(role) => handleOverrideStage(role)}
                  actionsDisabled={actionsDisabled || actionLoading}
                />
              ))}
            </div>

            {/* AI Recommendation */}
            {projectData.aiRecommendation && (
              <div
                className="mt-5 rounded-xl border border-puntt-accent-5/50 bg-puntt-accent-2/30 p-4"
                data-testid="project-status-ai-recommendation"
              >
                <Flex align="center" gap="2" className="mb-1.5">
                  <Lightning
                    size={14}
                    weight="fill"
                    className="text-puntt-accent-9"
                  />
                  <Text size="1" weight="bold" className="text-puntt-accent-11">
                    AI recommendation
                  </Text>
                </Flex>
                <Text
                  size="2"
                  className="leading-relaxed text-puntt-neutral-gray-12"
                >
                  {projectData.aiRecommendation}
                </Text>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
